import { DATA_UPDATE_FIELD_LABELS, DATA_UPDATE_GROUPS, getFieldsForGroups, isDataUpdateGroupId } from './dataUpdateGroups';
import type { DataUpdateChange } from './dpRequest.types';

type PlainRecord = Record<string, unknown>;

function isPlainObject(value: unknown): value is PlainRecord {
    return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function isEmptyValue(value: unknown): boolean {
    if (value === null || value === undefined) return true;
    if (typeof value === 'string') return value.trim() === '';
    if (Array.isArray(value)) return value.length === 0;
    if (isPlainObject(value)) return Object.values(value).every(isEmptyValue);
    return false;
}

function stripVolatileKeys(value: unknown): unknown {
    if (Array.isArray(value)) return value.map(stripVolatileKeys);
    if (isPlainObject(value)) {
        const result: PlainRecord = {};
        for (const key of Object.keys(value).sort()) {
            if (key === 'id') continue;
            result[key] = stripVolatileKeys(value[key]);
        }
        return result;
    }
    if (typeof value === 'string') return value.trim();
    return value;
}

export function sameDataValue(a: unknown, b: unknown): boolean {
    if (isEmptyValue(a) && isEmptyValue(b)) return true;
    return JSON.stringify(stripVolatileKeys(a)) === JSON.stringify(stripVolatileKeys(b));
}

export function fieldToGroupId(field: string): string | undefined {
    return DATA_UPDATE_GROUPS.find(group => group.fields.includes(field))?.id;
}

export function buildDataUpdateChanges(
    groupIds: readonly string[],
    current: PlainRecord,
    proposed: PlainRecord
): DataUpdateChange[] {
    const changes: DataUpdateChange[] = [];

    for (const field of getFieldsForGroups(groupIds)) {
        const before = current[field];
        const after = proposed[field];
        if (sameDataValue(before, after)) continue;

        changes.push({
            group: fieldToGroupId(field) ?? '',
            field,
            label: DATA_UPDATE_FIELD_LABELS[field] ?? field,
            before: before ?? null,
            after: after ?? null,
        });
    }

    return changes;
}

export type DataUpdatePayloadResult =
    | { ok: true; groups: string[]; fields: PlainRecord }
    | { ok: false; error: string };

export function sanitizeDataUpdatePayload(groupIds: unknown, payload: unknown): DataUpdatePayloadResult {
    if (!Array.isArray(groupIds) || groupIds.length === 0) {
        return { ok: false, error: 'Escolha pelo menos um item para atualizar.' };
    }

    const groups = Array.from(new Set(groupIds as unknown[]));
    if (!groups.every((id): id is string => typeof id === 'string' && isDataUpdateGroupId(id))) {
        return { ok: false, error: 'Um dos itens escolhidos não existe.' };
    }

    if (!isPlainObject(payload)) {
        return { ok: false, error: 'Dados da atualização inválidos.' };
    }

    const allowedFields = getFieldsForGroups(groups);
    const extra = Object.keys(payload).filter(key => !allowedFields.includes(key));
    if (extra.length > 0) {
        return { ok: false, error: 'A atualização tem campos fora dos itens escolhidos.' };
    }

    const fields: PlainRecord = {};
    for (const field of allowedFields) {
        if (field in payload) fields[field] = payload[field];
    }

    return { ok: true, groups, fields };
}
